import { useEffect, useState } from 'react';
import { useParams, useLocation } from 'wouter';
import { compradorTrpc, setSlugCompradorAtual } from '../../lib/compradorTrpc';
import { salvarSessaoComprador } from '../../lib/compradorAuth';
import { ehStandalone } from '../../lib/pwaInstall';
import { rotaApp } from './util';
import { CompradorShell } from '../../components/comprador/CompradorShell';

const INPUT_CLS =
  'h-[46px] w-full rounded-xl border border-line-2 bg-surface px-[15px] text-sm text-ink outline-none focus:border-accent';

interface OrgInfo {
  nome: string;
  logoUrl: string | null;
  corMarca: string | null;
}

/**
 * Tela de login do comprador (design oficial) — sem senha. "Acompanhe seu
 * apartamento": o comprador digita o e-mail do contrato e recebe um link
 * (15 min, uso único). Estado enviado tem Reenviar e Usar outro e-mail.
 * Linguagem simples, sem jargão de sistema.
 */
export function LoginComprador() {
  const { slug } = useParams<{ slug: string }>();
  const [, navigate] = useLocation();
  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [orgErro, setOrgErro] = useState(false);
  const [email, setEmail] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [reenviado, setReenviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [linkDeTeste, setLinkDeTeste] = useState<string | null>(null);
  // Entrada por SENHA (opcional, para quem criou uma). O padrão é o link mágico;
  // 'senha' só aparece quando o comprador clica no link discreto "já tenho senha".
  const [modo, setModo] = useState<'link' | 'senha'>('link');
  const [senha, setSenha] = useState('');
  const [erroSenha, setErroSenha] = useState<string | null>(null);
  // Login por POLLING: segredo do app; se já existir (pedido anterior), o efeito
  // abaixo já fica consultando — cobre "fechar o app, clicar o link, reabrir".
  const chavePendente = slug ? `ama_comprador_pendente_${slug}` : null;
  const [pendenteId, setPendenteId] = useState<string | null>(() =>
    chavePendente && typeof localStorage !== 'undefined' ? localStorage.getItem(chavePendente) : null,
  );

  // Enquanto houver um pendenteId (o app pediu um link), consulta a cada 3s se o
  // link já foi clicado em QUALQUER contexto. Quando sim, recebe a sessão no
  // PRÓPRIO storage do app e entra — resolvendo o app instalado que não vê a
  // sessão gravada pelo navegador.
  useEffect(() => {
    if (!slug || !pendenteId) return;
    let vivo = true;
    async function checar() {
      try {
        const r = await compradorTrpc.auth.consultarPendente.mutate({ orgSlug: slug!, pendenteId: pendenteId! });
        if (vivo && r.pronto) {
          if (chavePendente) localStorage.removeItem(chavePendente);
          salvarSessaoComprador(slug!, r.sessionToken);
          // Navegação DURA: o app já está em /app/<slug> (esta tela de login), e
          // um navigate() do wouter pra MESMA rota não re-avalia a sessão. O
          // reload garante que EntradaComprador veja a sessão e abra o Início.
          window.location.assign(rotaApp(slug!));
        }
      } catch {
        /* silencioso: continua tentando */
      }
    }
    void checar(); // imediato (cobre reabrir o app após clicar no link)
    const t = setInterval(() => void checar(), 3000);
    return () => {
      vivo = false;
      clearInterval(t);
    };
  }, [slug, pendenteId, chavePendente, navigate]);

  async function entrarComSenha(): Promise<void> {
    if (!slug) return;
    setErroSenha(null);
    setEnviando(true);
    try {
      const { sessionToken } = await compradorTrpc.auth.loginComSenha.mutate({ orgSlug: slug, email, senha });
      salvarSessaoComprador(slug, sessionToken);
      navigate(rotaApp(slug), { replace: true });
    } catch (e) {
      setErroSenha((e as Error).message || 'Não foi possível entrar.');
    } finally {
      setEnviando(false);
    }
  }

  useEffect(() => {
    setSlugCompradorAtual(slug ?? null);
    if (!slug) return;
    compradorTrpc.auth.orgInfo
      .query({ orgSlug: slug })
      .then(setOrg)
      .catch(() => setOrgErro(true));
  }, [slug]);

  async function solicitar(): Promise<void> {
    if (!slug) return;
    setErro(null);
    setEnviando(true);
    // Garante o segredo do polling antes de pedir o link (o token nasce ligado a
    // ele; o app instalado então recebe a sessão pelo consultarPendente).
    let pid = pendenteId;
    if (!pid) {
      pid = crypto.randomUUID();
      if (chavePendente) localStorage.setItem(chavePendente, pid);
      setPendenteId(pid);
    }
    try {
      const r = await compradorTrpc.auth.solicitarLink.mutate({ orgSlug: slug, email, pendenteId: pid });
      setEnviado(true);
      const preview = (r as { devLinkPreview?: string }).devLinkPreview;
      if (preview) setLinkDeTeste(preview);
    } catch {
      setErro('Não foi possível enviar o link agora. Tente de novo em instantes.');
    } finally {
      setEnviando(false);
    }
  }

  if (orgErro) {
    return (
      <CompradorShell>
        <p className="text-center text-sm text-ink-3" data-testid="comprador-org-nao-encontrada">
          Não encontramos um aplicativo para este endereço. Confira o link com a sua incorporadora.
        </p>
      </CompradorShell>
    );
  }

  if (enviado) {
    return (
      <CompradorShell logoUrl={org?.logoUrl} orgNome={org?.nome} corMarca={org?.corMarca}>
        <div className="text-center" data-testid="comprador-login-enviado">
          <div
            className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full"
            style={{ background: 'var(--brand-soft)', color: 'var(--brand)' }}
            aria-hidden="true"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
              <path
                d="M4 6h16v12H4z M4 6l8 7 8-7"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h1 className="font-serif text-[22px] font-medium text-ink">Confira seu e-mail</h1>
          <p className="mt-2 text-sm leading-relaxed text-ink-3">
            Enviamos um link de acesso para
            <br />
            <span className="font-semibold text-ink">{email}</span>
          </p>
          <p className="mt-2 text-sm leading-relaxed text-ink-3">
            Toque no link e você entra direto, sem digitar senha.
          </p>
          <p className="mt-2 text-[13px] font-medium" style={{ color: 'var(--brand, var(--accent))' }} data-testid="comprador-aguardando-link">
            Pode deixar este app aberto: assim que você tocar no link, ele entra sozinho aqui.
          </p>

          <div
            className="mt-5 rounded-xl px-4 py-3 text-left text-[13px] leading-relaxed"
            style={{ background: 'var(--surface-2)', color: 'var(--ink-2)' }}
          >
            O link vale por 15 minutos. Não chegou?{' '}
            <button
              type="button"
              disabled={enviando}
              onClick={() => {
                setReenviado(true);
                void solicitar();
              }}
              className="font-semibold hover:underline disabled:opacity-60"
              style={{ color: 'var(--brand)' }}
              data-testid="comprador-reenviar"
            >
              {enviando ? 'Reenviando…' : reenviado ? 'Reenviado' : 'Reenviar'}
            </button>
          </div>

          {linkDeTeste && (
            <div
              className="mt-4 rounded-xl border border-dashed p-3 text-left text-xs"
              style={{ borderColor: 'var(--line-2)' }}
              data-testid="comprador-link-teste"
            >
              <div className="mb-1 font-semibold text-ink-3">Ambiente de teste (não aparece em produção)</div>
              <a href={linkDeTeste} className="break-all hover:underline" style={{ color: 'var(--brand)' }}>
                {linkDeTeste}
              </a>
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              setEnviado(false);
              setReenviado(false);
              setLinkDeTeste(null);
              if (chavePendente) localStorage.removeItem(chavePendente);
              setPendenteId(null);
              setEmail('');
            }}
            className="mt-5 w-full rounded-xl border border-line-2 bg-surface py-3 text-sm font-medium text-ink-2"
          >
            Usar outro e-mail
          </button>
        </div>
      </CompradorShell>
    );
  }

  // ── Entrada por SENHA (só para quem criou; nunca obrigatória) ─────────────
  if (modo === 'senha') {
    return (
      <CompradorShell logoUrl={org?.logoUrl} orgNome={org?.nome} corMarca={org?.corMarca}>
        <h1 className="text-center font-serif text-[30px] font-medium leading-[1.15] text-ink">Entrar com senha</h1>
        <p className="mb-7 mt-3 text-center text-[15px] leading-relaxed text-ink-3">
          Se você criou uma senha no app, entre por aqui.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void entrarComSenha();
          }}
          className="space-y-4"
        >
          <div>
            <label className="mb-2 block text-[12.5px] font-medium text-ink-2" htmlFor="comprador-email-senha">
              Seu e-mail
            </label>
            <input
              id="comprador-email-senha"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="voce@email.com"
              className={INPUT_CLS}
              data-testid="comprador-email-senha-input"
            />
          </div>
          <div>
            <label className="mb-2 block text-[12.5px] font-medium text-ink-2" htmlFor="comprador-senha">
              Senha
            </label>
            <input
              id="comprador-senha"
              type="password"
              required
              autoComplete="current-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              placeholder="••••••••"
              className={INPUT_CLS}
              data-testid="comprador-senha-input"
            />
          </div>
          {erroSenha && (
            <div
              className="rounded-xl px-3 py-2.5 text-xs"
              style={{ background: 'var(--risk-soft)', border: '1px solid var(--risk-line)', color: 'var(--risk)' }}
            >
              {erroSenha}
            </div>
          )}
          <button
            type="submit"
            disabled={enviando}
            data-testid="comprador-entrar-senha"
            className="w-full rounded-2xl py-4 text-[15px] font-semibold text-white shadow-[0_12px_24px_-10px_rgba(0,0,0,0.45)] transition-transform active:translate-y-px disabled:opacity-60"
            style={{ background: 'var(--brand, var(--accent))' }}
          >
            {enviando ? 'Entrando…' : 'Entrar'}
          </button>
        </form>
        <div className="mt-5 flex flex-col items-center gap-2 text-center text-[13px]">
          <button
            type="button"
            onClick={() => void solicitar()}
            className="text-ink-3 hover:text-ink"
            data-testid="comprador-esqueci-senha"
          >
            Esqueci a senha (enviar link por e-mail)
          </button>
          <button type="button" onClick={() => setModo('link')} className="font-medium" style={{ color: 'var(--brand, var(--accent))' }}>
            Entrar pelo link
          </button>
        </div>
      </CompradorShell>
    );
  }

  return (
    <CompradorShell logoUrl={org?.logoUrl} orgNome={org?.nome} corMarca={org?.corMarca}>
      <h1 className="text-center font-serif text-[30px] font-medium leading-[1.15] text-ink">
        Acompanhe seu apartamento
      </h1>
      <p className="mb-7 mt-3 text-center text-[15px] leading-relaxed text-ink-3">
        Enviamos um link de acesso para o seu e-mail. Sem senha para lembrar.
      </p>
      {/* No app INSTALADO, o link do e-mail abre no navegador (contexto separado
          do app) e a sessão não fica no app — então tocar o ícone volta pra cá.
          Orientamos a entrar por SENHA, que grava a sessão no próprio app e
          persiste. Só aparece no app instalado. */}
      {ehStandalone() && (
        <div
          className="mb-6 rounded-xl px-4 py-3 text-left text-[13px] leading-relaxed"
          style={{ background: 'var(--surface-2)', color: 'var(--ink-2)' }}
          data-testid="comprador-login-standalone-dica"
        >
          Você está no app instalado. Para entrar direto sempre que abrir, use sua{' '}
          <button type="button" onClick={() => setModo('senha')} className="font-semibold underline" style={{ color: 'var(--brand, var(--accent))' }}>
            senha
          </button>
          . Ainda não tem? Entre uma vez pelo link e crie uma em Perfil.
        </div>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void solicitar();
        }}
        className="space-y-4"
      >
        <div>
          <label className="mb-2 block text-[12.5px] font-medium text-ink-2" htmlFor="comprador-email">
            Seu e-mail
          </label>
          <input
            id="comprador-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="voce@email.com"
            className={INPUT_CLS}
            data-testid="comprador-email-input"
          />
        </div>
        {erro && (
          <div
            className="rounded-xl px-3 py-2.5 text-xs"
            style={{ background: 'var(--risk-soft)', border: '1px solid var(--risk-line)', color: 'var(--risk)' }}
          >
            {erro}
          </div>
        )}
        <button
          type="submit"
          disabled={enviando}
          data-testid="comprador-enviar-link"
          className="w-full rounded-2xl py-4 text-[15px] font-semibold text-white shadow-[0_12px_24px_-10px_rgba(0,0,0,0.45)] transition-transform active:translate-y-px disabled:opacity-60"
          style={{ background: 'var(--brand, var(--accent))' }}
        >
          {enviando ? 'Enviando…' : 'Receber link de acesso'}
        </button>
      </form>
      {/* Link secundário DISCRETO — só para quem já criou senha. A entrada
          padrão continua sem senha. */}
      <div className="mt-5 text-center">
        <button
          type="button"
          onClick={() => setModo('senha')}
          className="text-[13px] text-ink-3 hover:text-ink"
          data-testid="comprador-ja-tenho-senha"
        >
          Já tenho senha
        </button>
      </div>
      {org && (
        <p className="mt-4 text-center text-xs leading-relaxed text-ink-4">
          Ao entrar, você concorda com os termos de uso da {org.nome}.
        </p>
      )}
    </CompradorShell>
  );
}
